// Estimated market prices based on rarity and attributes
// These simulate recent sale averages for Lorcana cards

const BASE_PRICES: Record<string, { low: number; mid: number; high: number }> = {
  Common:      { low: 0.05, mid: 0.15, high: 0.50 },
  Uncommon:    { low: 0.10, mid: 0.35, high: 1.00 },
  Rare:        { low: 0.25, mid: 1.50, high: 5.00 },
  'Super Rare': { low: 1.00, mid: 4.00, high: 12.00 },
  Epic:        { low: 2.00, mid: 6.00, high: 15.00 },
  Legendary:   { low: 5.00, mid: 15.00, high: 45.00 },
  Enchanted:   { low: 25.00, mid: 75.00, high: 250.00 },
  Special:     { low: 3.00, mid: 10.00, high: 30.00 },
  Iconic:      { low: 10.00, mid: 30.00, high: 80.00 },
};

// Newer sets tend to have lower prices (more supply)
const SET_AGE_MULTIPLIER: Record<string, number> = {
  'The First Chapter': 1.4,
  'Rise of the Floodborn': 1.2,
  'Into the Inklands': 1.1,
  "Ursula's Return": 1.0,
  'Shimmering Skies': 0.95,
  'Azurite Sea': 0.9,
  "Archazia's Island": 0.9,
  'Reign of Jafar': 0.85,
  'Whispers in the Well': 0.85,
  'Fabled': 1.3,
  'Winterspell': 0.8,
};

export interface CardPrice {
  low: number;
  mid: number;
  high: number;
  trend: 'up' | 'down' | 'stable';
  lastUpdated: string;
}

// Generate a deterministic but varied price from card attributes
function hashSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getCardPrice(card: {
  name: string;
  rarity: string;
  set_name: string;
  enchanted?: boolean | null;
  card_type?: string;
}): CardPrice {
  const base = BASE_PRICES[card.rarity] || BASE_PRICES.Common;
  const setMult = SET_AGE_MULTIPLIER[card.set_name] || 1.0;
  const seed = hashSeed(card.name + card.set_name);
  
  // Add some variance per card (±30%)
  const variance = 0.7 + (seed % 60) / 100;
  
  // Enchanted variants get a big boost
  const enchantedMult = card.enchanted ? 3.5 : 1.0;
  
  const low = Math.round(base.low * setMult * variance * enchantedMult * 100) / 100;
  const mid = Math.round(base.mid * setMult * variance * enchantedMult * 100) / 100;
  const high = Math.round(base.high * setMult * variance * enchantedMult * 100) / 100;
  
  // Deterministic trend based on name hash
  const trends: Array<'up' | 'down' | 'stable'> = ['up', 'down', 'stable'];
  const trend = trends[seed % 3];

  return {
    low,
    mid,
    high,
    trend,
    lastUpdated: 'Recent sales avg',
  };
}

export function formatPrice(price: number): string {
  return price < 1 ? `$${price.toFixed(2)}` : `$${price.toFixed(2)}`;
}
